import { useState, useEffect, useMemo } from 'react';
import moment from 'moment';
import { Button, Row, Col } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSquare } from '@fortawesome/free-solid-svg-icons';
import { DataGrid } from 'components/dataGrid/dataGrid';
import * as routes from '../../../routes';
import { updateAppointmentAttendance } from 'actions/appointmentActions';
import { clearSessionQuestionsAndAnswers } from 'actions/guidedResponseActions';
import { useAppDispatch, useAppSelector } from '../../../store';
import LoadingIndicator from 'components/loadingIndicator';
import Mhd2Permissions from '../../../constants/permissions';
import { useDataSource } from '../../../components/dataGrid';
import '../css/searchAppointmentResultsGrid.css';

interface AppointmentSearchResult {
    bookedBy: string;
    serviceName: string;
    speciality: string;
    slotDate: Date;
    appointmentTypeName: string;
    systemName: string;
    siteName: string;
    patientName: string;
    idBookedAppointment: number;
    birthDate?: Date | null;
    attendance: string;
    patientRefernceId?: string | null;
    slotEndDate: Date;
    providerResponse: string | null;
    reasonForVisit: string | null;
    appointmentReferenceId: string;
    payorType: string | null;
    decisionSupportSessionId: string | null
}

interface UpdateAppointmentCriteria {
    appointmentReferenceId: string;
    attendanceId: number;
    bookedAppointmentId: string;
}

interface AttendanceInfo {
    idAttendance: number;
    attendanceName: string;
}

function SearchAppointmentResultsGrid(props: any) {

    const {
        isDataEdited = false,
        setIsDataEdited,
        isEditAllowed = true,
        attendanceInfo,
        handleShowConfirmationModal,
        results,
        setResults,
        handleSearch,
        showGrid,
        isEditMode,
        setIsEditMode
    }: {
        isDataEdited: boolean,
        setIsDataEdited: Function,
        isEditAllowed: boolean,
        attendanceInfo: AttendanceInfo[],
        handleShowConfirmationModal: Function,
        results: any,
        setResults: any,
        handleSearch: any,
        showGrid: boolean,
        isEditMode: boolean,
        setIsEditMode: Function
    } = props

    const enabledEditPatientAttendance = useAppSelector((state) => state.config.appointmentManagement.enableEditPatientAttendance);
    const currentPermissions = useAppSelector((state) => state.auth.permissions)
    const hasEditPermission = currentPermissions.includes(Mhd2Permissions.appointmentManagement);
    const routePrefix = useAppSelector((state) => state.config.instance.routePrefix);

    const dispatch = useAppDispatch();

    const [updateAppointmentsCriteria, setUpdateAppointmentsCriteria] = useState<UpdateAppointmentCriteria[]>([]);
    const [isSaving, setIsSaving] = useState<boolean>(false);
    const [isCanceling, setIsCanceling] = useState<boolean>(false);
    const TIME_FORMAT = 'hh:mm A';
    const DATE_FORMAT = 'MM/DD/YYYY'
    const [currentResults, setCurrentResults] = useState<any>({});

    const dataSource = useDataSource(currentResults, setResults, handleSearch);

    //Creating new array because AG grid inline edit doesn't accept complex objects
    const attendanceNames = useMemo(
        () => attendanceInfo.map(x => x.attendanceName),
        [attendanceInfo]
    );

    // copied component from ag grid, in future if we upgrade ag grid this need to be updated accorndingly
    const AgGridSelectComponent = (props: any) => {
        return (
            <div
               className="ag-cell ag-cell-normal-height ag-column-first ag-cell-focus ag-cell-inline-editing"
                style={{ left: "0px", width: "100%" }}
            >
                <div className="ag-cell-wrapper" role="presentation">
                    <div className="ag-cell-edit-wrapper">
                        <div
                            className="ag-cell-editor ag-labeled ag-label-align-left ag-select"
                        >
                            <div
                                className="ag-label ag-hidden"
                            ></div>
                            <div
                                className="ag-wrapper ag-picker-field-wrapper ag-picker-collapsed"
                                role="combobox"
                            >
                                <div
                                    className="ag-picker-field-display"
                                >
                                    {props.data.attendance}
                                </div>
                                <div className="ag-picker-field-icon" data-ref="eIcon" aria-hidden="true">
                                    <span
                                        className="ag-icon ag-icon-small-down"
                                    ></span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    useEffect(() => {
        //Updating edited results between page flips
        if (updateAppointmentsCriteria.length > 0 && results?.data?.results) {
            setCurrentResults(results);
            const updatedResults = results.data.results.map((item: any) => {
                const updatedAttendance = updateAppointmentsCriteria.find(
                    (criteria) => criteria.appointmentReferenceId === item.appointmentReferenceId
                );
                if (updatedAttendance) {
                    return { ...item, attendance: attendanceInfo.find(a => a.idAttendance === updatedAttendance.attendanceId)?.attendanceName || item.attendance };
                }
                return item;
            });

            setCurrentResults((prev: any) => ({
                ...prev,
                data: {
                    ...prev.data,
                    results: updatedResults
                }
            }));
        }
        else {
            setCurrentResults(results);
        }
    }, [results]);

    useEffect(() => {
        clearContext();
    }, [isEditMode]);

    // Dev Note: Add sorting function in the service call if you are adding a new column here or set sortable to false
    const columnDefs = [
        {
            field: 'attendance',
            headerName: 'Patient Attendance',
            cellEditor: 'agSelectCellEditor',
            sortable: false,
            cellEditorParams: {
                values: attendanceNames,
                valueListGap: 10,
            },
            colId: isEditMode ? 'button' : '',
            editable: (r: any) => isEditMode && isEditAllowed && enabledEditPatientAttendance && hasEditPermission && !moment(r.data.slotDate).isAfter(moment()),
            cellRenderer: (r: any) => {
                if (isEditMode && isEditAllowed && enabledEditPatientAttendance && hasEditPermission && !moment(r.data.slotDate).isAfter(moment())) {
                    return (
                        <AgGridSelectComponent data={r.data}/>
                    )
                }
                else {
                    return <span>{r.data.attendance ? r.data.attendance.trim() : ''}</span>;
                }
            }
        },
        {
            field: 'serviceName',
            headerName: 'Service',
            cellRenderer: (r: any) => <span>{r.data.serviceName ? r.data.serviceName.trim() : '-'}</span>
        },
        {
            field: 'speciality',
            headerName: 'Speciality',
            cellRenderer: (r: any) => <span>{r.data.speciality ? r.data.speciality.trim() : '-'}</span>
        },
        {
            field: 'appointmentTypeName',
            headerName: 'Appointment Type',
            cellRenderer: (r: any) => <span>{r.data.appointmentTypeName ? r.data.appointmentTypeName.trim() : '-'}</span>
        },
        {
            field: 'slotDate',
            headerName: 'On',
            cellClass: (r: any) => {
                return isEditMode ? (moment(r.data.slotDate).isAfter(moment())) ? 'slotNotEditable' : 'slotEditable' : '';
            },
            cellRenderer: (r: any) => <span>{r.data.slotDate ? moment(r.data.slotDate).format(DATE_FORMAT) : '-'}</span>
        },
        {
            field: 'slotDate',
            headerName: 'At',
            cellRenderer: (r: any) => <span>{r.data.slotDate ? moment(r.data.slotDate).format(TIME_FORMAT) : '-'}</span>
        },
        {
            field: 'slotEndDate',
            headerName: 'Till',
            cellRenderer: (r: any) => <span>{r.data.slotEndDate ? moment(r.data.slotEndDate).format(TIME_FORMAT) : '-'}</span>
        },
        {
            field: 'patientName',
            headerName: 'Patient Name',
            cellRenderer: (r: any) => <span>{r.data.patientName ? r.data.patientName.trim() : '-'}</span>
        },
        {
            field: 'birthDate',
            headerName: 'Date Of Birth',
            cellRenderer: (r: any) => <span>{r.data.birthDate ? moment(r.data.birthDate).format(DATE_FORMAT) : '-'}</span>
        },
        {
            field: 'payorType',
            headerName: 'Payor Type',
            cellRenderer: (r: any) => <span>{r.data.payorType ? r.data.payorType.trim() : '-'}</span>
        },
        {
            field: 'providerResponse',
            headerName: 'Provider Response',
            cellRenderer: (r: any) => <span>{r.data.providerResponse ? r.data.providerResponse.trim() : '-'}</span>
        },
        {
            field: 'reasonForVisit',
            headerName: 'Reason For Visit',
            cellRenderer: (r: any) => <span>{r.data.reasonForVisit ? r.data.reasonForVisit.trim() : '-'}</span>
        },
        {
            field: 'systemName',
            headerName: 'From System',
            cellRenderer: (r: any) => <span>{r.data.systemName ? r.data.systemName.trim() : '-'}</span>
        },
        {
            field: 'siteName',
            headerName: 'From Site',
            cellRenderer: (r: any) => <span>{r.data.siteName ? r.data.siteName.trim() : '-'}</span>
        },
        {
            field: 'bookedBy',
            headerName: 'Booked By',
            cellRenderer: (r: any) => <span>{r.data.bookedBy ? r.data.bookedBy.trim() : '-'}</span>,
            sortable: false
        }
    ];

    const redirectToAppointmentDetails = (appointment: AppointmentSearchResult, routePrefix: any) => {
        if (!appointment?.decisionSupportSessionId) {
            dispatch(clearSessionQuestionsAndAnswers());
        }
        dispatch(routes.appointmentDetails(routePrefix, appointment.appointmentReferenceId));
    }

    const handleRowClick = (e: any, appointment: AppointmentSearchResult) => {
        e.preventDefault();
        if (isDataEdited) {
            setResults((prev: any) => ({
                ...prev,
                loading: false
            }));
            handleShowConfirmationModal(() => redirectToAppointmentDetails(appointment, routePrefix));
        }
        else {
            redirectToAppointmentDetails(appointment, routePrefix);
        }
    }

    const handleCellEdit = (e: any) => {
        if (e.data.attendance !== e.newValue && e.newValue !== 'Select') {
            setCurrentResults((prevResults: any) => {
                if (!prevResults?.data?.results) return prevResults;

                const updatedResults = prevResults.data.results.map((item: any) =>
                    item.appointmentReferenceId === e.data.appointmentReferenceId
                        ? { ...item, attendance: e.newValue }
                        : item
                );

                return {
                    ...prevResults,
                    data: {
                        ...prevResults.data,
                        results: updatedResults
                    }
                };
            });

            const selectedAttendance = attendanceInfo.find(x => x.attendanceName == e.newValue);
            if (selectedAttendance) {
                const newObject: UpdateAppointmentCriteria = {
                    appointmentReferenceId: e.data.appointmentReferenceId,
                    attendanceId: selectedAttendance.idAttendance,
                    bookedAppointmentId: e.data.idBookedAppointment
                };

                const existingIndex = updateAppointmentsCriteria.findIndex((item) => item.appointmentReferenceId === newObject.appointmentReferenceId);

                if (existingIndex !== -1) {
                    // Update the existing object
                    const updatedData = [...updateAppointmentsCriteria];
                    updatedData[existingIndex] = { ...updatedData[existingIndex], ...newObject };
                    setUpdateAppointmentsCriteria(updatedData);
                } else {
                    // Add the new object
                    setUpdateAppointmentsCriteria((prev) => [...prev, newObject]);
                }
            }

            setIsDataEdited(true);
        }
    }

    const clearContext = () => {
        setUpdateAppointmentsCriteria([]);
        setCurrentResults(results);
        setIsSaving(false);
        setIsCanceling(false);
        setIsDataEdited(false);
    }

    const handleSave = async (e: any) => {
        e.preventDefault();
        setIsSaving(true);
        await dispatch(updateAppointmentAttendance(updateAppointmentsCriteria));
        setIsEditMode(false);
        setIsSaving(false);
    }

    const handleCancel = (e: any) => {
        e.preventDefault();
        if (isDataEdited) {
            handleShowConfirmationModal(cancelEditMode);
        }
        else {
            setIsEditMode(false);
            setIsCanceling(true);
        }
    }

    const cancelEditMode = () => {
        setIsEditMode(false);
        setIsCanceling(true);
    }

    const handleEdit = (e: any) => {
        e.preventDefault();
        setIsEditMode(true);
    }

    return (
        <>
            {enabledEditPatientAttendance && showGrid &&
                <>
                    <Row className='siteReportFilterContent'>
                        <Col>
                            {!isEditMode ?
                            <Button color='primary' disabled={isEditMode || !isEditAllowed || !enabledEditPatientAttendance || !results || !results.data || results.data.results.length == 0 || !hasEditPermission} onClick={handleEdit}>Edit</Button>
                                : <>
                                    <Button color='primary' style={{ marginLeft: '5px' }} disabled={!isDataEdited || !isEditAllowed} onClick={handleSave}>
                                        {isSaving ? (
                                            <span>
                                                <FontAwesomeIcon icon='spinner' spin /> Saving...
                                            </span>
                                        ) : (
                                            <span>Save</span>
                                        )}
                                    </Button>
                                    <Button color='danger' style={{ marginLeft: '5px' }} onClick={handleCancel}>
                                        {isCanceling ? (
                                            <span>
                                                <FontAwesomeIcon icon='spinner' spin /> Canceling...
                                            </span>
                                        ) : (
                                            <span>Cancel</span>
                                        )}
                                    </Button>
                                </>
                            }
                        </Col>
                        {isEditMode && 
                            <Col className="legendColumn">
                                <FontAwesomeIcon icon={faSquare} className="legendForEditable" /> <span className="legendForEditableSpan">Editable</span>
                                <FontAwesomeIcon icon={faSquare} className="legendForNonEditable" /> <span>Uneditable</span>
                            </Col>
                        }
                    </Row>
                    <br />
                </>
            }
            {isSaving ?
                <LoadingIndicator />
                : <>
                    {showGrid && 
                        <div>
                            <DataGrid
                                columnDefs={columnDefs}
                                serverSideDatasource={dataSource}
                                onRowClick={handleRowClick}
                                noRowsMessage='No appointments found.'
                                onCellEdit={handleCellEdit}
                                singleClickEdit={true}
                            />
                        </div>
                    }
                </>
            }
        </>
    )
}

export default SearchAppointmentResultsGrid;